import type { IconProps } from "@obs/icons";
import { colors, measure } from "@obs/tokens/index.stylex.ts";
import * as stylex from "@stylexjs/stylex";
import type { ReactElement } from "react";

import type { Semantic } from "../../../../utils/semantic";
import { Typography } from "../../../Typography";

export type DataProps = {
	data?: DataContentProps;
	dataInfo?: DataInfoContent;
	disabled?: boolean;
	selected?: boolean;
};

const styles = stylex.create({
	root: {
		display: "flex",
		flexDirection: "column",
		rowGap: measure.spacingGapMin,
		justifyContent: "center",
		alignItems: "flex-end",
	},
	dataContent: {
		display: "flex",
		alignItems: "center",
		gap: measure.spacingGapMin,
	},
	infoDefault: {
		color: colors.neutralTxtModerate,
		textAlign: "end",
	},
	infoSelected: {
		color: colors.neutralTxtInverted,
		textAlign: "end",
	},
});

const semanticDataStyles = stylex.create({
	default: { color: colors.neutralTxtDefault },
	accent: { color: colors.accentTxtDefault },
	secondary: { color: colors.neutralTxtModerate },
	disabled: { color: colors.disabledTxtDefault },
	success: { color: colors.successTxtDefault },
	warning: { color: colors.warningTxtDefault },
	danger: { color: colors.dangerTxtDefault },
	selected: { color: colors.neutralTxtInverted },
});

export const Data = ({ data, dataInfo, disabled, selected }: DataProps) => {
	return (
		<span {...stylex.props(styles.root)}>
			{data && <DataContent {...data} semantic={disabled ? "disabled" : data.semantic} selected={selected} />}
			{dataInfo && <DataInfo {...dataInfo} disabled={disabled} selected={selected} />}
		</span>
	);
};

type DataContentProps = {
	semantic?: Semantic;
	content: string;
	disabled?: boolean;
	selected?: boolean;
	icon?: {
		Component: (props: IconProps) => ReactElement;
	} & Omit<IconProps, "size" | "iconColor">;
};

const DataContent = ({ semantic, selected, icon, ...priceProps }: DataContentProps) => {
	const colorStyle = selected ? semanticDataStyles.selected : semanticDataStyles[semantic || "default"];

	return (
		<Typography.TitleSecondary render={<span />} sx={[styles.dataContent, colorStyle]}>
			{icon && <icon.Component size={24} iconColor="currentColor" />}
			{priceProps.content}
		</Typography.TitleSecondary>
	);
};

type DataInfoContent = {
	content: string;
	disabled?: boolean;
	selected?: boolean;
};

const DataInfo = ({ selected, ...dataInfo }: DataInfoContent) => {
	return (
		<Typography.BodyDefault render={<span />} sx={selected ? styles.infoSelected : styles.infoDefault}>
			{dataInfo.content}
		</Typography.BodyDefault>
	);
};
